import { newTaskSuffix } from '@griply/types';
import { onDeadlineDeadlineReorder } from '@griply/utils';
import { addDays, endOfDay } from 'date-fns';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import {
  noDeadlineSectionID,
  overdueSectionID,
} from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';
import { parseDateKey } from 'shared/utils/format-date-key';

import { useDeadlineDeadlineTree } from '../../hooks/use-deadline-deadline-tree';
import { GroupedListProps } from '../../types';
import { DeadlineHeader } from '../deadline-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './deadline-deadline-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task];

export const DeadlineDeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDeadlineDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onDeadlineDeadlineReorder({ updatedItems, tasksMap });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const dateStr = item.sourceId.replace(newTaskSuffix, '');
          const deadlineDeadline =
            dateStr === noDeadlineSectionID
              ? undefined
              : dateStr === overdueSectionID
                ? endOfDay(addDays(today, -1))
                : parseDateKey(dateStr);

          return (
            <NewTaskRow
              deadlineDeadline={deadlineDeadline}
              startTime={null}
              duration={null}
              goal={groupSettings.goalId}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // Date
        if (type === TaskListItemTypes.Date) {
          return (
            <DeadlineHeader denseFormat={denseFormat} item={itemInstance} />
          );
        }

        return null;
      },
    [
      today,
      denseFormat,
      groupSettings.displayDue,
      groupSettings.goalId,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
    ],
  );

  const [tree] = useTree();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
